/**
 * v11.6 V116-4-5 (ADR-0114 §5–§7, design round V116-4-1; build review v116-track4): who can read a signed pack.
 *  - InsurerAccessCard: ONE list of everyone who can read the pack: keyed verify links (insurer or broker) and API
 *    data access grants, each labelled. Every member sees the links; owners and admins also see the API grants and
 *    create or revoke (the server enforces the same). Revoking says what else that recipient still has.
 *  - CreateLinkDialog: kind, recipient, optional email, expiry (default 90 days). The URL carries the key and is
 *    shown ONCE. Warns when the latest signed version is behind the draft. API data access is a secondary option.
 *  - WithdrawDialog: a signed withdrawal record on one version; the reason is required.
 */
import { useEffect, useId, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Badge, Button, Checkbox, Field, Input, Radio, Textarea } from '@aigovops/design-system';
import {
  createPackLink, listCdsGrants, listPackLinks, revokeCdsGrant, revokePackLink, withdrawInsurabilityPack,
  type CdsGrantRow, type PackLink,
} from '../../lib/api';
import { fmtDate } from '../../lib/format';
import { ActionDialog, errorText } from '../today/shared';
import './insurability-pack.css';

/** One sentence everywhere (design round R2-H9 / JR2-08). */
export const RECALL_TEXT = 'A signed version can be withdrawn, but anyone who already opened it may keep a copy.';

const KIND_LABEL: Record<PackLink['kind'], string> = { insurer: 'Insurer link', broker: 'Broker link' };
const STATUS_LABEL: Record<PackLink['status'], string> = { active: 'Active', expired: 'Expired', revoked: 'Revoked' };
/** CDS package kinds that carry the insurability pack. */
export const INSURER_KINDS = new Set(['ai_inventory', 'ai_disclosure', 'loss_history']);

export const packLinksKey = (slug: string) => ['insurability-links', slug] as const;
export const cdsGrantsKey = (slug: string) => ['cds-grants', slug] as const;

/** Names match across the two kinds of access ignoring case, spacing and a trailing "(…)" note. */
const sameName = (a: string, b: string) => {
  const n = (s: string) => s.replace(/\(.*\)\s*$/, '').trim().toLowerCase().replace(/\s+/g, ' ');
  return n(a) === n(b);
};

type Row =
  | { kind: 'link'; id: string; name: string; link: PackLink }
  | { kind: 'api'; id: string; name: string; grant: CdsGrantRow };

export function InsurerAccessCard({ slug, canManage, hasSigned, onCreateLink, onShareApi, onSignFirst, announce }: {
  slug: string;
  canManage: boolean;
  /** A signed version exists (links and grants share signed versions only). */
  hasSigned: boolean;
  onCreateLink: () => void;
  onShareApi: () => void;
  onSignFirst: (() => void) | null;
  announce: (m: string) => void;
}) {
  const qc = useQueryClient();
  const links = useQuery({ queryKey: packLinksKey(slug), queryFn: () => listPackLinks(slug) });
  const grants = useQuery({ queryKey: cdsGrantsKey(slug), queryFn: () => listCdsGrants(slug), enabled: canManage });
  const [target, setTarget] = useState<Row | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const rows: Row[] = [
    ...(links.data?.links ?? []).map((l) => ({ kind: 'link' as const, id: l.link_id, name: l.recipient_name, link: l })),
    ...(grants.data?.grants ?? [])
      .filter((g) => g.status === 'active' && g.package_kinds.some((k) => INSURER_KINDS.has(k)))
      .map((g) => ({ kind: 'api' as const, id: g.grant_id, name: g.agency_name, grant: g })),
  ];
  /** What else the recipient being revoked can still read. */
  const others = target
    ? rows.filter((r) => r.id !== target.id && sameName(r.name, target.name) && (r.kind === 'api' || r.link.status === 'active'))
    : [];

  async function revoke(): Promise<boolean> {
    if (!target) return false;
    setBusy(true);
    setError(null);
    try {
      if (target.kind === 'link') {
        await revokePackLink(slug, target.id, reason.trim() || undefined);
        await qc.invalidateQueries({ queryKey: packLinksKey(slug) });
        announce(`${target.name}’s link no longer opens the pack.`);
      } else {
        await revokeCdsGrant(slug, target.id);
        await qc.invalidateQueries({ queryKey: cdsGrantsKey(slug) });
        announce(`${target.name} can no longer read the pack as data.`);
      }
      setTarget(null);
      // A11Y-T4-03: focus lands on a stable element, the card heading.
      setTimeout(() => document.getElementById('ip-access-h')?.focus(), 0);
      return true;
    } catch (err) {
      setError(errorText(err, 'Could not revoke the access.'));
      return false;
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="ip-card" aria-labelledby="ip-access-h" id="ip-access">
      <div className="ip-card-head">
        <h2 id="ip-access-h" tabIndex={-1} className="ip-h2">Insurer access</h2>
        {canManage && hasSigned ? (
          <span className="ip-acts">
            <Button variant="ghost" compact onClick={onShareApi}>Give API data access</Button>
            <Button id="ip-create-link" variant="secondary" compact onClick={onCreateLink}>Create a link</Button>
          </span>
        ) : null}
      </div>
      {links.isLoading ? <p className="ip-muted">Loading…</p> : links.error ? (
        <Alert variant="error">Could not load who has access. <Button variant="ghost" compact onClick={() => void links.refetch()}>Retry</Button></Alert>
      ) : rows.length === 0 ? (
        <p className="ip-muted ip-flat">
          No insurer or broker can read the pack.{' '}
          {!canManage ? 'An owner or admin shares it.'
            : hasSigned ? <Button variant="secondary" compact onClick={onCreateLink}>Create a link</Button>
            : onSignFirst ? <Button variant="secondary" compact onClick={onSignFirst}>Sign the pack first</Button> : null}
        </p>
      ) : (
        <ul className="ip-rows" data-testid="ip-links">
          {rows.map((r) => (
            <li key={`${r.kind}-${r.id}`} className="ip-row">
              <span className="ip-row-name">{r.name}</span>
              <span className="ip-dim">{r.kind === 'link' ? KIND_LABEL[r.link.kind] : 'API data access'}</span>
              {r.kind === 'link'
                ? (r.link.status === 'active' ? <Badge tone="accent">{STATUS_LABEL[r.link.status]}</Badge> : <Badge>{STATUS_LABEL[r.link.status]}</Badge>)
                : <Badge tone="accent">Active</Badge>}
              <span className="ip-dim">
                {r.kind === 'link'
                  ? <>{r.link.status === 'revoked' ? `Revoked ${fmtDate(r.link.revoked_at)}` : `${r.link.status === 'expired' ? 'Expired' : 'Expires'} ${fmtDate(r.link.expires_at)}`}{' · '}{r.link.last_opened_at ? `last opened ${fmtDate(r.link.last_opened_at)}` : 'not opened yet'}</>
                  : <>Expires {fmtDate(r.grant.expires_at)} · {r.grant.last_access_at ? `last read ${fmtDate(r.grant.last_access_at)}` : 'not read yet'}</>}
              </span>
              {canManage && (r.kind === 'api' || r.link.status === 'active') ? (
                <Button variant="secondary" compact onClick={() => { setError(null); setReason(''); setTarget(r); }}
                  aria-label={`Revoke ${r.name}’s ${r.kind === 'link' ? 'link' : 'API data access'}`}>
                  Revoke
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      <ActionDialog
        open={target !== null}
        title={target ? `Revoke ${target.name}’s ${target.kind === 'link' ? 'link' : 'API data access'}?` : 'Revoke access?'}
        submitLabel="Revoke"
        danger
        onClose={() => setTarget(null)}
        onSubmit={revoke}
        busy={busy}
        error={error}
      >
        <p className="ip-body">
          {target?.kind === 'api'
            ? 'Their key stops working now. To share again, create new access.'
            : 'The link stops working now, including for anyone it was forwarded to.'}{' '}{RECALL_TEXT}
        </p>
        {target ? (
          <p className="ip-body" data-testid="ip-revoke-others">
            {others.length
              ? `${target.name} still has: ${others.map((o) => o.kind === 'link' ? `${KIND_LABEL[o.link.kind].toLowerCase()} until ${fmtDate(o.link.expires_at)}` : `API data access until ${fmtDate(o.grant.expires_at)}`).join(', ')}. Revoke it separately.`
              : `${target.name} has no other access to the pack.`}
          </p>
        ) : null}
        {target?.kind === 'link' ? (
          <Field id="ip-revoke-reason" label="Reason (optional)">
            <Input value={reason} maxLength={500} onChange={(e) => setReason(e.currentTarget.value)} />
          </Field>
        ) : null}
      </ActionDialog>
    </section>
  );
}

/** What the share form holds, kept by the page while the user signs first (WCAG 3.3.7: never typed twice). */
export interface LinkDraft { kind: 'insurer' | 'broker'; name: string; email: string; days: string }

export function CreateLinkDialog({ slug, latest, draftVersion, draftBehind, onSignFirst, onShareApi, onClose, onCreated, draft, onDraft, notice }: {
  slug: string;
  latest: { package_id: string; version: number };
  draftVersion: number;
  draftBehind: boolean;
  onSignFirst: (() => void) | null;
  /** Secondary: API data access for the recipient's systems team. */
  onShareApi: (() => void) | null;
  onClose: () => void;
  onCreated: (name: string) => void;
  /** The form as the user left it (after "Sign first"), and where to keep it. */
  draft?: LinkDraft | null;
  onDraft?: (d: LinkDraft) => void;
  /** Set when the dialog comes back after signing: "Version N signed." is said here (the page behind is inert). */
  notice?: string | null;
}) {
  const uid = useId();
  const [kind, setKind] = useState<'insurer' | 'broker'>(draft?.kind ?? 'insurer');
  const [name, setName] = useState(draft?.name ?? '');
  const [email, setEmail] = useState(draft?.email ?? '');
  const [days, setDays] = useState(draft?.days ?? '90');
  useEffect(() => { onDraft?.({ kind, name, email, days }); }, [kind, name, email, days, onDraft]);
  const [errs, setErrs] = useState<{ name?: string; email?: string; days?: string }>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [saved, setSaved] = useState(false);
  useEffect(() => { if (url) setTimeout(() => document.getElementById(`${uid}-copy`)?.focus(), 0); }, [url, uid]);
  const doneHint = `${uid}-done-hint`;

  async function submit(): Promise<boolean> {
    if (url) {
      if (!saved) { setError('Copy the link, or tick “I’ve saved the link”, before closing. It can’t be shown again.'); return false; }
      onClose();
      return true;
    }
    const e: typeof errs = {};
    const n = Number(days);
    if (!name.trim()) e.name = 'Name who receives the link.';
    if (email.trim() && !/^[^@\s]+@[^@\s]+\.[a-z]{2,}$/i.test(email.trim())) e.email = 'Enter a valid email, or leave it empty.';
    if (!Number.isInteger(n) || n < 1 || n > 365) e.days = 'Pick 1 to 365 days.';
    setErrs(e);
    if (e.name || e.email || e.days) {
      const first = e.name ? 'name' : e.email ? 'email' : 'days';
      setTimeout(() => document.getElementById(`${uid}-${first}`)?.focus(), 0);
      return false;
    }
    setBusy(true);
    setError(null);
    try {
      const out = await createPackLink(slug, {
        package_id: latest.package_id, kind, recipient_name: name.trim(), expires_in_days: n,
        ...(email.trim() ? { recipient_email: email.trim() } : {}),
      });
      setUrl(out.url);
      onCreated(name.trim());
      return false;
    } catch (err) {
      setError(errorText(err, 'Could not create the link.'));
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function copy() {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setSaved(true);
      setError(null);
    } catch {
      setError('Copy failed. Select the link and copy it, then tick the box below.');
    }
  }

  function close() {
    if (url && !saved) { setError('This link is shown once. Copy it, or tick “I’ve saved the link”, before closing.'); return; }
    onClose();
  }

  return (
    <ActionDialog open title="Share with an insurer or broker" submitLabel={url ? 'Done' : 'Create link'} onClose={close} onSubmit={submit} busy={busy} error={error}
      cancelLabel={url ? 'Close' : 'Cancel'} submitDisabled={!!url && !saved} describedBy={url && !saved ? doneHint : undefined}
      subtitle={url ? undefined : `The link opens version ${latest.version} and any later version you sign, until it expires or you revoke it.`}>
      {url ? (
        <div className="ip-stack">
          <Alert variant="success">Link created for {name.trim()}. It is shown once: copy it now and send it yourself.</Alert>
          <Field id={`${uid}-url`} label="Link">
            <Input readOnly value={url} onFocus={(e) => e.currentTarget.select()} data-testid="ip-link-url" />
          </Field>
          <div className="ip-acts">
            <Button id={`${uid}-copy`} variant="primary" compact onClick={copy}>{copied ? 'Copied' : 'Copy link'}</Button>
            <Checkbox label="I’ve saved the link" checked={saved} onChange={(e) => { setSaved(e.currentTarget.checked); if (e.currentTarget.checked) setError(null); }} />
          </div>
          {!saved ? <p id={doneHint} className="ip-dim ip-flat">Copy the link or tick the box to finish.</p> : null}
          {/* A11Y-T4-05: a polite announcement, not only the button label. */}
          <p className="sr-only" role="status" aria-live="polite">{copied ? 'Link copied to the clipboard.' : ''}</p>
        </div>
      ) : (
        <>
          {notice ? <Alert variant="success"><span role="status" data-testid="ip-link-signed">{notice}</span></Alert> : null}
          {draftBehind && !notice ? (
            <Alert variant="warning">
              <span data-testid="ip-link-behind">Version {draftVersion} isn’t signed · this link shows version {latest.version} until you sign.</span>
              {onSignFirst ? <> <Button variant="secondary" compact onClick={onSignFirst}>Sign first</Button></> : null}
            </Alert>
          ) : null}
          <fieldset className="ip-fieldset">
            <legend className="ip-legend">Who it’s for</legend>
            <Radio id={`${uid}-k-insurer`} name={`${uid}-kind`} label="Insurer" checked={kind === 'insurer'} onChange={() => setKind('insurer')} />
            <Radio id={`${uid}-k-broker`} name={`${uid}-kind`} label="Broker" checked={kind === 'broker'} onChange={() => setKind('broker')} />
          </fieldset>
          <Field id={`${uid}-name`} label="Recipient" required error={errs.name} hint="Shown in the access list and to the reader">
            <Input value={name} maxLength={200} onChange={(e) => { setName(e.currentTarget.value); setErrs((x) => ({ ...x, name: undefined })); }} />
          </Field>
          <Field id={`${uid}-email`} label="Email (optional)" error={errs.email} hint="For your records; we don’t email it">
            <Input type="email" value={email} onChange={(e) => { setEmail(e.currentTarget.value); setErrs((x) => ({ ...x, email: undefined })); }} />
          </Field>
          <Field id={`${uid}-days`} label="Expires in (days)" error={errs.days} hint="1 to 365 days">
            <Input type="number" min={1} max={365} inputMode="numeric" value={days} onChange={(e) => { setDays(e.currentTarget.value); setErrs((x) => ({ ...x, days: undefined })); }} />
          </Field>
          {onShareApi ? (
            <p className="ip-muted ip-flat">
              Their systems team needs the pack as data?{' '}
              <Button variant="ghost" compact onClick={onShareApi}>Give API data access instead</Button>
            </p>
          ) : null}
        </>
      )}
    </ActionDialog>
  );
}

export function WithdrawDialog({ slug, pack, onClose, onDone }: {
  slug: string;
  pack: { package_id: string; version: number } | null;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const uid = useId();
  const [reason, setReason] = useState('');
  const [err, setErr] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { if (pack) { setReason(''); setErr(undefined); setError(null); } }, [pack]);
  return (
    <ActionDialog
      open={pack !== null}
      title={pack ? `Withdraw version ${pack.version}?` : 'Withdraw this version?'}
      submitLabel="Withdraw version"
      danger
      onClose={onClose}
      busy={busy}
      error={error}
      onSubmit={async () => {
        if (!pack) return false;
        if (reason.trim().length < 3) {
          setErr('Say why it is withdrawn (3 characters or more).');
          setTimeout(() => document.getElementById(`${uid}-reason`)?.focus(), 0);
          return false;
        }
        setBusy(true);
        setError(null);
        try {
          await withdrawInsurabilityPack(slug, pack.package_id, reason.trim());
          onDone(`Version ${pack.version} withdrawn.`);
          return true;
        } catch (e) {
          setError(errorText(e, 'Could not withdraw the version.'));
          return false;
        } finally {
          setBusy(false);
        }
      }}
    >
      <p className="ip-body">Readers see “Withdrawn”, your name and this reason, as a signed record. {RECALL_TEXT}</p>
      <Field id={`${uid}-reason`} label="Reason" required error={err}>
        <Textarea rows={3} maxLength={1000} value={reason} placeholder="e.g. Published in error" onChange={(e) => { setReason(e.currentTarget.value); setErr(undefined); }} />
      </Field>
    </ActionDialog>
  );
}
